import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useBoolean } from 'react-use'

import { Field, Label, ModalGrid, SimpleInput } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { useParamId } from '@/hooks/useParamId'
import { useReqError } from '@/hooks/useReqError'
import { validateTableName } from '@/shared/ag-grid'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE } from '@/shared/constants'
import { useDebounceInput, useFormEnter } from '@/shared/makyo-hooks'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

const FORM_ID = 'create-table'

/**
 * this is the create table modal view
 *
 * @todo:
 *  use logic from DbClientContext and GridV2Context only
 *
 */
export function CreateTableModal() {
  const { t } = useTranslation()
  const orgId = useParamId('org')
  const isProject = useIsProjectDb()
  const { project } = useSingleProjectContext()

  const { dbId, dbClient, tables, showCreateTable, setShowCreateTable, setShowCreateTableOptions } =
    useDbClientContext()
  const { table } = useGridV2Context()

  const [error, setError] = useState('')
  const [hasError, setHasError] = useBoolean(false)

  const printAPIError = useReqError()

  const inputRef = useRef<HTMLInputElement>(null)

  const [debounceInput, cancelDebounceInput, setDebounceInput, tableName, setTableName] = useDebounceInput('')
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  useEffect(() => {
    if (isBtnLoading) return
    if (debounceInput) {
      setError(
        validateTableName(
          debounceInput,
          tables.filter((tb) => tb.id !== table?.id)
        )
      )
    }
  }, [tables, table?.id, debounceInput, isBtnLoading])

  const reset = useCallback(() => {
    setIsBtnLoading(false)
    setShowCreateTable(false)
    setError('')
    setHasError(false)
    cancelDebounceInput()
    setDebounceInput('')
    setTableName('')
  }, [setIsBtnLoading, setShowCreateTable, setHasError, cancelDebounceInput, setDebounceInput, setTableName])

  const navigate = useNavigate()

  const handleSubmit = async () => {
    if (!dbClient || isBtnLoading) return

    setIsBtnLoading(true)

    try {
      // create new dbTable
      const newTable = await dbClient.CreateTable(tableName)

      // if (isProject) {
      //   await new Promise<void>((resolve) => {
      //     const onPageProcessFinished = () => {
      //       window.removeEventListener(EVENT_KEYS.PAGE_PROCESS_FINISHED, onPageProcessFinished)
      //       resolve()
      //     }
      //     window.addEventListener(EVENT_KEYS.PAGE_PROCESS_FINISHED, onPageProcessFinished)

      //     window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_CREATED, { detail: newTable.id }))
      //   })
      // }

      // redirect to new table
      if (orgId) {
        navigate(`${ORG_ROUTE}/${orgId}/${ORG_DATABASE_ROUTE}/${dbId}/${newTable.id}`)
      } else if (isProject) {
        navigate(`${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${newTable.id}`)
      }

      window.dispatchEvent(new CustomEvent('db-client-reload'))
      toast.success(t('Table created'))
      //
    } catch (err) {
      printAPIError(err as any)
    } finally {
      reset()
      setIsBtnLoading(false)
    }
  }

  useFormEnter(FORM_ID, () => {
    if (isBtnLoading) return
    handleSubmit()
  })

  useEffect(() => {
    if (showCreateTable && inputRef.current) {
      inputRef.current.focus()
    }
  }, [showCreateTable])

  return (
    <ModalGrid
      id={FORM_ID}
      show={showCreateTable}
      onHide={() => {
        if (isBtnLoading) return
        reset()
      }}
      onCancel={() => {
        if (isBtnLoading) return
        reset()
        setShowCreateTableOptions(true)
      }}
      title={t('Create Table')}
      isLoading={isBtnLoading}
      disabled={hasError}
      primaryTrailing={t('Created')}
      onConfirm={handleSubmit}
      leadingTitle={t('Go back')}
    >
      <form
        id={FORM_ID}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <Field>
          <Label required>{t('Name')}</Label>
          <SimpleInput
            ref={inputRef}
            error={t(error)}
            autoFocus={true}
            required
            value={tableName}
            onChange={(e) => {
              const val = e.currentTarget.value
              setHasError(validateTableName(val, tables) !== '')
              setTableName(val)
            }}
          />
        </Field>
      </form>
    </ModalGrid>
  )
}
